import { FC } from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { SOCIAL_SETTINGS } from './socialSettings';

import AppleTouchIcon from '../../../public/assets/apple-touch-icon.png';
import Favicon32 from '../../../public/assets/favicon-32x32.png';
import Favicon16 from '../../../public/assets/favicon-16x16.png';
import FaviconIco from '../../../public/assets/favicon.ico';
import SafariPinnedTab from '../../../public/assets/safari-pinned-tab.svg';
import BrowserconfigXML from '../../../public/assets/browserconfig.xml';
import SiteWebmanifest from '../../../public/assets/site.webmanifest';

import { MAIN_SITE_LINK } from '../../../constants';
import {
    OpenGraphItems,
    OpenGraphItem,
    ExtraLinkItems,
    ExtraLinkItem,
    ExtraMetaTags
} from './types';

export const buildCanonicalUrl = (path: string) => {
    const pathWithoutForwardSlashPrefix = path.replace(/^\//g, '');

    return `${MAIN_SITE_LINK}/${pathWithoutForwardSlashPrefix}`;
};

export const getSocialSharingTags = (path: string) => {
    const iPhoneUrl = `${SOCIAL_SETTINGS.IPHONE_BASE_URL}${path}`;
    const twitterPlayUrl = `${SOCIAL_SETTINGS.TWITTER_PLAY_BASE_URL}${path}`;
    return (
        <>
            <meta name="twitter:card" content="summary" />
            <meta
                name="twitter:app:name:iphone"
                content={SOCIAL_SETTINGS.TWITTER_IOS_APP_NAME}
            />
            <meta
                name="twitter:app:id:iphone"
                content={SOCIAL_SETTINGS.IOS_APP_ID}
            />
            <meta
                name="twitter:app:name:googleplay"
                content={SOCIAL_SETTINGS.TWITTER_ANDROID_APP_NAME}
            />
            <meta
                name="twitter:app:id:googleplay"
                content={SOCIAL_SETTINGS.ANDROID_APP_ID}
            />
            <meta name="twitter:app:url:iphone" content={iPhoneUrl} />
            <meta name="twitter:app:url:googleplay" content={twitterPlayUrl} />
            <meta property="fb:admins" content={SOCIAL_SETTINGS.FB_ADMINS} />
            <meta
                property="twitter:account_id"
                content={SOCIAL_SETTINGS.TWITTER_ACCOUNT_ID}
            />
        </>
    );
};

export const generateOpenGraphTags = (items: OpenGraphItems) => (
    <>
        {items.map(({ property, content }: OpenGraphItem) => (
            <meta
                key={`${property}-${content}`}
                property={property}
                content={content}
            />
        ))}
    </>
);

export const generateExtraLinkTags = (items: ExtraLinkItems) => (
    <>
        {items.map(({ href, rel, crossOrigin }: ExtraLinkItem) => (
            <link key={href} href={href} rel={rel} crossOrigin={crossOrigin} />
        ))}
    </>
);

type Props = {
    pageTitle: string;
    pageDescription: string;
    canonicalPath: string;
    index?: () => boolean;
    schema?: () => string;
    renderSocialSharingTags?: boolean;
    openGraphValues?: OpenGraphItems;
    extraLinkTags?: ExtraLinkItems;
    extraMetaTags?: ExtraMetaTags;
};

const Head: FC<Props> = ({
    pageTitle,
    pageDescription,
    canonicalPath,
    index,
    schema,
    renderSocialSharingTags,
    openGraphValues,
    extraLinkTags,
    children,
    extraMetaTags
}) => {
    const router = useRouter();
    const { asPath } = router;

    return (
        <NextHead>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            {canonicalPath && (
                <link rel="canonical" href={buildCanonicalUrl(canonicalPath)} />
            )}
            <link
                rel="apple-touch-icon"
                sizes="180x180"
                href={AppleTouchIcon}
            />
            <link rel="icon" type="image/png" href={Favicon32} sizes="32x32" />
            <link rel="icon" type="image/png" href={Favicon16} sizes="16x16" />
            <link rel="shortcut icon" href={FaviconIco} />
            <link rel="manifest" href={SiteWebmanifest} />
            <link rel="mask-icon" href={SafariPinnedTab} color="#8046f1" />
            {extraLinkTags ? generateExtraLinkTags(extraLinkTags) : null}
            <meta name="msapplication-config" content={BrowserconfigXML} />
            <meta name="msapplication-TileColor" content="#8046f1" />
            <meta name="theme-color" content="#ffffff" />
            {renderSocialSharingTags ? getSocialSharingTags(asPath) : null}
            {openGraphValues && openGraphValues.length > 0
                ? generateOpenGraphTags(openGraphValues)
                : null}
            {index && !index() ? (
                <meta name="robots" content="noindex, follow" />
            ) : null}
            {extraMetaTags
                ? extraMetaTags.map(({ name, content }, index) => (
                      <meta
                          key={`${name}-${index}`}
                          name={name}
                          content={content}
                      />
                  ))
                : null}
            {schema ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: schema()
                    }}
                />
            ) : null}
            {children}
        </NextHead>
    );
};

export default Head;
